import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';

interface Banner {
  id: string;
  type: 'HOMEPAGE' | 'PROMOTIONAL' | 'COURSE';
  title: string;
  subtitle: string | null;
  linkUrl: string | null;
  linkLabel: string | null;
}

interface Announcement {
  id: string;
  title: string;
  body: string;
}

/** Internal paths stay in the app; anything else opens as a normal link. */
function Cta({ href, label }: { href: string; label: string }) {
  return href.startsWith('/') ? (
    <Link className="btn btn--sm" to={href}>
      {label}
    </Link>
  ) : (
    <a className="btn btn--sm" href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

/**
 * What Site Content has scheduled for the front of the site: live homepage and
 * promotional banners, and announcements addressed to everyone.
 *
 * The server decides what is live (each item's window is applied there), so
 * this only renders what comes back — and renders nothing at all when nothing
 * is scheduled, rather than an empty frame.
 */
export function SiteBanners() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  useEffect(() => {
    let cancelled = false;
    api<{ items: Banner[] }>('/site/banners')
      .then((result) => {
        // Course banners belong on their course, not the front page.
        if (!cancelled) setBanners(result.items.filter((item) => item.type !== 'COURSE'));
      })
      .catch(() => undefined);
    api<{ items: Announcement[] }>('/site/announcements')
      .then((result) => {
        if (!cancelled) setAnnouncements(result.items.slice(0, 2));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (banners.length === 0 && announcements.length === 0) return null;

  return (
    <div className="site-promos">
      {announcements.map((item) => (
        <div className="site-notice" role="note" key={item.id}>
          <span className="site-notice__tag">News</span>
          <div>
            <strong>{item.title}</strong>
            {/* Sanitised by the API, on save and again when served. */}
            <div className="site-notice__body" dangerouslySetInnerHTML={{ __html: item.body }} />
          </div>
        </div>
      ))}

      {banners.length > 0 ? (
        <div className="site-banners">
          {banners.map((banner) => (
            <article
              className={`site-banner site-banner--${banner.type.toLowerCase()}`}
              key={banner.id}
            >
              {banner.type === 'PROMOTIONAL' ? (
                <span className="site-banner__tag">Offer</span>
              ) : null}
              <h2 className="site-banner__title">{banner.title}</h2>
              {banner.subtitle ? <p className="site-banner__text">{banner.subtitle}</p> : null}
              {banner.linkUrl ? (
                <Cta href={banner.linkUrl} label={banner.linkLabel || 'Learn more'} />
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
